/**
 * AnimationControlBar - Prop-driven animation controls for in-place playback.
 * Unlike AnimationControls which reads from AnimationContext,
 * this component accepts an AnimationInstance directly.
 */

import { useCallback } from 'react'
import { Pause, Play, Repeat, RotateCcw, X } from 'lucide-react'
import { Button } from '../ui/Button'
import { Slider } from '../ui/Slider'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select'
import type { AnimationInstance } from '@/hooks/UseAnimationInstance'
import type { PlaybackSpeed } from '@/types/Animation.types'
import { ANIMATION_DEFAULTS, SPEED_LABELS } from '@/types/Animation.types'
import { useAnimationProgress, useFormattedDuration, useFormattedTime } from '@/hooks/UseAnimationEngine'
import './animation-control-bar.css'

type AnimationControlBarProps = {
	instance: AnimationInstance
	onStop: () => void
}

export function AnimationControlBar({ instance, onStop }: AnimationControlBarProps) {
	const { state } = instance

	const progress = useAnimationProgress(state)
	const currentTime = useFormattedTime(state)
	const totalDuration = useFormattedDuration(state)

	const handlePlayPause = useCallback(() => {
		instance.toggle()
	}, [instance])

	const handleScrubberChange = useCallback(
		(value: number[]) => {
			const newProgress = (value[0] ?? 0) / 100
			instance.seek(newProgress)
		},
		[instance],
	)

	const handleSpeedChange = useCallback(
		(value: string) => {
			const speed = parseFloat(value) as PlaybackSpeed
			instance.setSpeed(speed)
		},
		[instance],
	)

	return (
		<div className="animation-control-bar">
			{/* Progress scrubber */}
			<div className="animation-controls-scrubber">
				<span className="animation-controls-time">
					{currentTime}
				</span>
				<Slider
					value={[progress]}
					onValueChange={handleScrubberChange}
					max={100}
					step={0.1}
					className="flex-1"
					aria-label="Animation progress"
				/>
				<span className="animation-controls-time">
					{totalDuration}
				</span>
			</div>

			{/* Control buttons */}
			<div className="animation-controls-buttons">
				<Button
					variant="ghost"
					size="icon"
					onClick={instance.reset}
					aria-label="Reset animation"
					className="animation-controls-button-reset"
				>
					<RotateCcw className="size-5" />
				</Button>

				<Button
					variant="action"
					onClick={handlePlayPause}
					aria-label={state.isPlaying ? 'Pause animation' : 'Play animation'}
					className="animation-controls-button-play"
				>
					{state.isPlaying ? (
						<Pause className="size-6" />
					) : (
						<Play className="ml-0.5 size-6" />
					)}
				</Button>

				<Button
					variant="ghost"
					size="icon"
					onClick={instance.toggleLoop}
					aria-label={`Toggle loop: ${state.loopMode ? 'on' : 'off'}`}
					className="animation-controls-button-loop"
					data-active={state.loopMode}
				>
					<Repeat className="size-5" />
				</Button>

				{/* Visual divider */}
				<div className="animation-controls-divider" />

				{/* Speed control */}
				<div className="animation-controls-speed-group">
					<span className="animation-controls-speed-label">Speed:</span>
					<Select
						value={String(state.playbackSpeed)}
						onValueChange={handleSpeedChange}
					>
						<SelectTrigger
							className="animation-controls-speed-select"
							aria-label="Playback speed"
						>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{ANIMATION_DEFAULTS.SPEED_OPTIONS.map((speed) => (
								<SelectItem key={speed} value={String(speed)}>
									{SPEED_LABELS[speed]}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				{/* Visual divider */}
				<div className="animation-controls-divider" />

				{/* Stop button */}
				<Button
					variant="ghost"
					size="icon"
					onClick={onStop}
					aria-label="Stop animation"
					className="animation-control-bar-stop"
				>
					<X className="size-5" />
				</Button>
			</div>
		</div>
	)
}
